/**
 * A ficha do paciente na tela (#811): o formulário, o corpo que vai para a
 * API e os textos de cada valor. Sem React, para o teste não precisar de tela.
 *
 * ── A ficha vai inteira ──────────────────────────────────────────────────
 *
 * Ao contrário do cadastro, que manda só o que mudou, a ficha substitui tudo
 * (`PUT`): campo em branco é apagar. A versão lida vai junto, e é ela que
 * impede duas pessoas de apagarem uma o que a outra escreveu.
 */

import type { FichaDoPaciente, SalvarFichaDoPaciente } from '@cliqcrm/contracts';

export const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

type EstadoCivil = NonNullable<FichaDoPaciente['estado_civil']>;
type Canal = NonNullable<FichaDoPaciente['canal_preferido']>;
type Horario = NonNullable<FichaDoPaciente['horario_preferido']>;

export const NOMES_DE_ESTADO_CIVIL: Readonly<Record<EstadoCivil, string>> = {
  solteiro: 'Solteiro(a)',
  casado: 'Casado(a)',
  uniao_estavel: 'União estável',
  separado: 'Separado(a)',
  divorciado: 'Divorciado(a)',
  viuvo: 'Viúvo(a)',
};

export const NOMES_DE_CANAL: Readonly<Record<Canal, string>> = {
  whatsapp: 'WhatsApp',
  telefone: 'Telefone',
  email: 'E-mail',
};

export const NOMES_DE_HORARIO: Readonly<Record<Horario, string>> = {
  manha: 'Manhã',
  tarde: 'Tarde',
  noite: 'Noite',
};

/** O formulário: tudo texto, como os campos guardam. */
export interface FormularioDaFicha {
  nomeSocial: string;
  nascimento: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  profissao: string;
  estadoCivil: string;
  emergenciaNome: string;
  emergenciaTelefone: string;
  canalPreferido: string;
  horarioPreferido: string;
}

/** O formulário nasce com o que está gravado; o ausente vira campo em branco. */
export function formularioDe(ficha: FichaDoPaciente): FormularioDaFicha {
  return {
    nomeSocial: ficha.nome_social ?? '',
    nascimento: ficha.nascimento ?? '',
    cep: mascararCep(ficha.endereco.cep ?? ''),
    logradouro: ficha.endereco.logradouro ?? '',
    numero: ficha.endereco.numero ?? '',
    complemento: ficha.endereco.complemento ?? '',
    bairro: ficha.endereco.bairro ?? '',
    cidade: ficha.endereco.cidade ?? '',
    uf: ficha.endereco.uf ?? '',
    profissao: ficha.profissao ?? '',
    estadoCivil: ficha.estado_civil ?? '',
    emergenciaNome: ficha.emergencia.nome ?? '',
    emergenciaTelefone: ficha.emergencia.telefone ?? '',
    canalPreferido: ficha.canal_preferido ?? '',
    horarioPreferido: ficha.horario_preferido ?? '',
  };
}

/** Texto aparado, e o branco como ausente: é o que apaga do outro lado. */
function ouNulo(valor: string): string | null {
  const aparado = valor.trim();
  return aparado === '' ? null : aparado;
}

/** O corpo do `PUT`: a ficha inteira e a versão que a tela leu. */
export function corpoDe(
  formulario: FormularioDaFicha,
  versao: number | null,
): SalvarFichaDoPaciente {
  return {
    nome_social: ouNulo(formulario.nomeSocial),
    nascimento: ouNulo(formulario.nascimento),
    endereco: {
      // Só os dígitos: o traço é da tela, e a API guarda os oito números.
      cep: ouNulo(formulario.cep.replace(/\D/g, '')),
      logradouro: ouNulo(formulario.logradouro),
      numero: ouNulo(formulario.numero),
      complemento: ouNulo(formulario.complemento),
      bairro: ouNulo(formulario.bairro),
      cidade: ouNulo(formulario.cidade),
      uf: ouNulo(formulario.uf) as SalvarFichaDoPaciente['endereco']['uf'],
    },
    profissao: ouNulo(formulario.profissao),
    estado_civil: ouNulo(formulario.estadoCivil) as SalvarFichaDoPaciente['estado_civil'],
    emergencia: {
      nome: ouNulo(formulario.emergenciaNome),
      telefone: ouNulo(formulario.emergenciaTelefone),
    },
    canal_preferido: ouNulo(formulario.canalPreferido) as SalvarFichaDoPaciente['canal_preferido'],
    horario_preferido: ouNulo(
      formulario.horarioPreferido,
    ) as SalvarFichaDoPaciente['horario_preferido'],
    versao,
  };
}

/** `13010050` → `13010-050`, enquanto se digita. O que não é dígito sai. */
export function mascararCep(texto: string): string {
  const digitos = texto.replace(/\D/g, '').slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
}

/** A ficha sem nada preenchido: a tela oferece preencher em vez de mostrar vazios. */
export function fichaEmBranco(ficha: FichaDoPaciente): boolean {
  const { versao: _versao, endereco, emergencia, ...resto } = ficha;
  return [...Object.values(resto), ...Object.values(endereco), ...Object.values(emergencia)].every(
    (valor) => valor === null,
  );
}

/** `1992-03-12` → `12/03/1992`, sem passar por `Date`: o fuso trocaria o dia. */
export function dataPorExtenso(data: string): string {
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** A idade completa em `hoje` (`AAAA-MM-DD`). */
export function idadeEm(nascimento: string, hoje: string): number {
  const [anoN, mesN, diaN] = nascimento.split('-').map(Number) as [number, number, number];
  const [anoH, mesH, diaH] = hoje.split('-').map(Number) as [number, number, number];
  const fezAniversario = mesH > mesN || (mesH === mesN && diaH >= diaN);
  return anoH - anoN - (fezAniversario ? 0 : 1);
}

/** Hoje, no calendário de quem está na tela, como `AAAA-MM-DD`. */
export function hojeLocal(agora: Date = new Date()): string {
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/** "Rua das Flores, 120, apto 3": a primeira linha do endereço, ou `null`. */
export function ruaDe(endereco: FichaDoPaciente['endereco']): string | null {
  const partes = [endereco.logradouro, endereco.numero, endereco.complemento].filter(
    (parte): parte is string => parte !== null,
  );
  return partes.length > 0 ? partes.join(', ') : null;
}

/** "Centro, Campinas/SP, CEP 13010-050": a segunda linha, ou `null`. */
export function lugarDe(endereco: FichaDoPaciente['endereco']): string | null {
  const cidade =
    endereco.cidade && endereco.uf
      ? `${endereco.cidade}/${endereco.uf}`
      : (endereco.cidade ?? endereco.uf);
  const partes = [
    endereco.bairro,
    cidade,
    endereco.cep ? `CEP ${mascararCep(endereco.cep)}` : null,
  ].filter((parte): parte is string => parte !== null);
  return partes.length > 0 ? partes.join(', ') : null;
}

/** "WhatsApp, à tarde", "Telefone", "De manhã", ou `null`. */
export function preferenciaDe(ficha: FichaDoPaciente): string | null {
  const canal = ficha.canal_preferido ? NOMES_DE_CANAL[ficha.canal_preferido] : null;
  const horario = ficha.horario_preferido
    ? { manha: 'de manhã', tarde: 'à tarde', noite: 'à noite' }[ficha.horario_preferido]
    : null;
  if (canal && horario) return `${canal}, ${horario}`;
  if (canal) return canal;
  if (horario) return horario.charAt(0).toUpperCase() + horario.slice(1);
  return null;
}
